import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";

import { useApi } from "../hooks/useApi";
import {
  createTelegramLink,
  deleteNotification,
  loadNotificationPreferences,
  loadTelegramStatus,
  loadNotifications,
  loadNotificationsSummary,
  markAllNotificationsRead,
  markNotificationRead,
  saveNotificationPreference,
  unlinkTelegram,
} from "../services/notifications";
import { LEVEL_ICON, LEVEL_LABEL, groupByDay, itemTime, relativeTime } from "../utils/notificationsView";
import logger from "../utils/logger";
import "./NotificationCenter.css";

const POLL_INTERVAL_MS = 60000;
const PAGE = 30;

// Человек выделил текст уведомления, чтобы скопировать, — это не клик «открыть».
const hasTextSelection = () => {
  try {
    return Boolean(window.getSelection && window.getSelection().toString().trim());
  } catch (error) {
    return false;
  }
};

function BellIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" />
      <path d="M13.7 21a2 2 0 0 1-3.4 0" />
    </svg>
  );
}

/**
 * Колокольчик в шапке: счётчик непрочитанных, история по дням, фильтр по категории,
 * отметка прочтения и настройки каналов (в сервисе / почта / Telegram — по мере подключения).
 */
function NotificationCenter({ token, onOpen }) {
  const { request } = useApi();
  const [open, setOpen] = useState(false);
  const [view, setView] = useState("list"); // list | settings
  const [onlyUnread, setOnlyUnread] = useState(true);
  const [category, setCategory] = useState("");
  const [summary, setSummary] = useState({ unread_total: 0, unread_by_category: {}, categories: {} });
  const [items, setItems] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [prefs, setPrefs] = useState(null);
  const [tgWaiting, setTgWaiting] = useState(false);
  const [tgError, setTgError] = useState("");
  const rootRef = useRef(null);
  const panelRef = useRef(null);
  const [pos, setPos] = useState({ top: 64, right: 12 });

  const refreshSummary = useCallback(async () => {
    if (!token) return;
    try {
      setSummary(await loadNotificationsSummary(request));
    } catch (error) {
      logger.error(error);
    }
  }, [request, token]);

  const loadList = useCallback(async (append = false, from = 0) => {
    if (!token) return;
    setLoading(true);
    try {
      const data = await loadNotifications(request, { unreadOnly: onlyUnread, category, limit: PAGE, offset: from });
      const list = Array.isArray(data) ? data : [];
      setItems((prev) => (append ? [...prev, ...list] : list));
      setHasMore(list.length === PAGE);
    } catch (error) {
      logger.error(error);
    } finally {
      setLoading(false);
    }
  }, [request, token, onlyUnread, category]);

  useEffect(() => {
    if (!token) return undefined;
    refreshSummary();
    const timer = window.setInterval(refreshSummary, POLL_INTERVAL_MS);
    return () => window.clearInterval(timer);
  }, [token, refreshSummary]);

  useEffect(() => {
    if (open && view === "list") loadList(false, 0);
  }, [open, view, loadList]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (event) => { if (event.key === "Escape") setOpen(false); };
    const onClick = (event) => {
      const inside = (rootRef.current && rootRef.current.contains(event.target))
        || (panelRef.current && panelRef.current.contains(event.target));
      if (!inside) setOpen(false);
    };
    // Панель живёт в body (иначе фильтр/трансформация карточки шапки ломают position: fixed), поэтому
    // место под колокольчиком считаем сами.
    const place = () => {
      const rect = rootRef.current?.getBoundingClientRect();
      if (rect) setPos({ top: Math.round(rect.bottom + 10), right: Math.max(8, Math.round(window.innerWidth - rect.right)) });
    };
    place();
    window.addEventListener("keydown", onKey);
    window.addEventListener("mousedown", onClick);
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("mousedown", onClick);
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
    };
  }, [open]);

  useEffect(() => {
    if (open && view === "settings" && !prefs) {
      loadNotificationPreferences(request).then(setPrefs).catch((error) => logger.error(error));
    }
  }, [open, view, prefs, request]);

  const groups = useMemo(() => groupByDay(items), [items]);
  const categories = summary.categories || {};

  const readOne = async (item) => {
    if (!item.is_read) {
      try {
        await markNotificationRead(request, item.id);
      } catch (error) {
        logger.error(error);
      }
      setItems((prev) => (onlyUnread ? prev.filter((x) => x.id !== item.id) : prev.map((x) => (x.id === item.id ? { ...x, is_read: true } : x))));
      refreshSummary();
    }
  };

  const openItem = async (item) => {
    await readOne(item);
    if (item.link && onOpen) {
      setOpen(false);
      onOpen(item.link);
    }
  };

  const removeItem = async (item) => {
    try {
      await deleteNotification(request, item.id);
    } catch (error) {
      logger.error(error);
    }
    setItems((prev) => prev.filter((x) => x.id !== item.id));
    refreshSummary();
  };

  const readAll = async () => {
    try {
      await markAllNotificationsRead(request, category);
    } catch (error) {
      logger.error(error);
    }
    await loadList(false, 0);
    refreshSummary();
  };

  // После перехода в бота ждём, пока он подтвердит привязку: опрашиваем статус до двух минут.
  useEffect(() => {
    if (!tgWaiting) return undefined;
    let ticks = 0;
    const timer = setInterval(async () => {
      ticks += 1;
      try {
        const status = await loadTelegramStatus(request);
        if (status.linked) {
          setPrefs((prev) => (prev ? { ...prev, telegram_linked: true } : prev));
          setTgWaiting(false);
        }
      } catch (error) {
        logger.error(error);
      }
      if (ticks >= 40) setTgWaiting(false);
    }, 3000);
    return () => clearInterval(timer);
  }, [tgWaiting, request]);

  const linkTelegram = async () => {
    setTgError("");
    try {
      const data = await createTelegramLink(request);
      if (data?.url) {
        window.open(data.url, "_blank", "noopener,noreferrer");
        setTgWaiting(true);
      }
    } catch (error) {
      logger.error(error);
      setTgError("Не удалось создать ссылку для Telegram.");
    }
  };

  const unlinkTg = async () => {
    setTgError("");
    try {
      await unlinkTelegram(request);
      setPrefs((prev) => (prev ? { ...prev, telegram_linked: false } : prev));
    } catch (error) {
      logger.error(error);
      setTgError("Не удалось отвязать Telegram.");
    }
  };

  const togglePref = async (eventType, channel, enabled) => {
    setPrefs((prev) => ({
      ...prev,
      events: prev.events.map((event) => (event.event_type === eventType
        ? { ...event, channels: { ...event.channels, [channel]: { ...event.channels[channel], enabled } } }
        : event)),
    }));
    try {
      await saveNotificationPreference(request, eventType, channel, enabled);
    } catch (error) {
      logger.error(error);
      setPrefs(await loadNotificationPreferences(request));
    }
  };

  if (!token) return null;
  const unread = summary.unread_total || 0;

  return (
    <div className="ncenter" ref={rootRef}>
      <button
        type="button"
        className="ncenter__bell icon-button"
        onClick={() => setOpen((value) => !value)}
        aria-label={unread ? `Уведомления: ${unread} непрочитанных` : "Уведомления"}
        aria-expanded={open}
        title="Уведомления"
      >
        <BellIcon />
        {unread > 0 && <span className="ncenter__badge">{unread > 99 ? "99+" : unread}</span>}
      </button>

      {open && createPortal(
        <div className="ncenter__panel" role="dialog" aria-label="Центр уведомлений" ref={panelRef} style={{ top: pos.top, right: pos.right }}>
          <div className="ncenter__head">
            <strong>{view === "settings" ? "Настройки уведомлений" : "Уведомления"}</strong>
            <div className="ncenter__head-actions">
              {view === "list" && unread > 0 && (
                <button type="button" className="ncenter__link" onClick={readAll}>Прочитать все</button>
              )}
              <button type="button" className="ncenter__link" onClick={() => setView(view === "settings" ? "list" : "settings")}>
                {view === "settings" ? "← К списку" : "Настройки"}
              </button>
            </div>
          </div>

          {view === "list" && (
            <>
              <div className="ncenter__filters">
                <button type="button" className={`ncenter__chip${onlyUnread ? " is-on" : ""}`} onClick={() => setOnlyUnread(true)}>
                  Непрочитанные
                </button>
                <button type="button" className={`ncenter__chip${!onlyUnread ? " is-on" : ""}`} onClick={() => setOnlyUnread(false)}>
                  Все
                </button>
                <select
                  className="ncenter__select"
                  value={category}
                  onChange={(event) => setCategory(event.target.value)}
                  aria-label="Категория"
                >
                  <option value="">Все категории</option>
                  {Object.entries(categories).map(([key, label]) => (
                    <option key={key} value={key}>
                      {label}{summary.unread_by_category?.[key] ? ` (${summary.unread_by_category[key]})` : ""}
                    </option>
                  ))}
                </select>
              </div>

              <div className="ncenter__list">
                {groups.map((group) => (
                  <section key={group.label}>
                    <h4 className="ncenter__day">{group.label}</h4>
                    {group.items.map((item) => (
                      <article key={item.id} className={`ncenter__item ncenter__item--${item.level}${item.is_read ? " is-read" : ""}`}>
                        <span className="ncenter__icon" title={LEVEL_LABEL[item.level] || ""} aria-hidden="true">
                          {LEVEL_ICON[item.level] || "i"}
                        </span>
                        <div
                          className="ncenter__body"
                          role="link"
                          tabIndex={0}
                          onClick={() => { if (!hasTextSelection()) openItem(item); }}
                          onKeyDown={(event) => { if (event.key === "Enter") openItem(item); }}
                        >
                          <span className="ncenter__title">
                            {item.title}
                            {item.occurrences > 1 && <span className="ncenter__times">×{item.occurrences}</span>}
                          </span>
                          <span className="ncenter__text">{item.message}</span>
                          <span className="ncenter__meta">
                            {categories[item.category] || item.category} · {relativeTime(itemTime(item))}
                          </span>
                        </div>
                        <button type="button" className="ncenter__x" aria-label="Удалить уведомление" onClick={() => removeItem(item)}>×</button>
                      </article>
                    ))}
                  </section>
                ))}
                {!loading && items.length === 0 && (
                  <p className="ncenter__empty">
                    {onlyUnread ? "Непрочитанных нет. Нажмите «Все», чтобы увидеть историю." : "Пока здесь пусто."}
                  </p>
                )}
                {hasMore && (
                  <button type="button" className="ncenter__more" disabled={loading} onClick={() => loadList(true, items.length)}>
                    Показать ещё
                  </button>
                )}
              </div>
            </>
          )}

          {view === "settings" && (
            <div className="ncenter__settings">
              {!prefs && <p className="ncenter__empty">Загружаем…</p>}
              {prefs && (
                <>
                  <p className="ncenter__hint">Куда присылать каждое событие. Важные уведомления в сервисе отключить нельзя.</p>
                  <div className="ncenter__tg">
                    {prefs.telegram_linked ? (
                      <>
                        <span>Telegram привязан</span>
                        <button type="button" className="ncenter__more" onClick={unlinkTg}>Отвязать</button>
                      </>
                    ) : (
                      <>
                        <span>{tgWaiting ? "Нажмите Start в боте — ждём подтверждения…" : "Telegram не привязан"}</span>
                        <button type="button" className="ncenter__more" disabled={tgWaiting} onClick={linkTelegram}>Привязать Telegram</button>
                      </>
                    )}
                  </div>
                  {tgError && <p className="ncenter__hint" role="alert">{tgError}</p>}
                  <table className="ncenter__prefs">
                    <thead>
                      <tr>
                        <th>Событие</th>
                        {["inapp", "email", "telegram"].map((channel) => (
                          <th key={channel}>{prefs.channel_labels[channel]}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {prefs.events.map((event) => (
                        <tr key={event.event_type}>
                          <td>
                            {event.label}
                            <small>{prefs.category_labels[event.category] || event.category}</small>
                          </td>
                          {["inapp", "email", "telegram"].map((channel) => {
                            const state = event.channels[channel];
                            return (
                              <td key={channel}>
                                {state.available ? (
                                  <input
                                    type="checkbox"
                                    checked={state.enabled}
                                    disabled={state.locked}
                                    onChange={(e) => togglePref(event.event_type, channel, e.target.checked)}
                                    aria-label={`${event.label}: ${prefs.channel_labels[channel]}`}
                                  />
                                ) : (
                                  <span className="ncenter__soon" title="Канал подключается">скоро</span>
                                )}
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </>
              )}
            </div>
          )}
        </div>,
        document.body
      )}
    </div>
  );
}

export default NotificationCenter;
